import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  getPesquisaPublicaInfo,
  responderPesquisaPublica,
  verificarPesquisaPublica,
} from '../../api/formsApi';
import { PesquisaPublicaView } from '../formularios/interfaces/forms';
import CampoDaPesquisa from './CampoDaPesquisa';
import { formatPhoneBr } from '../../utils/phoneMask';
import logoBackground from '../auth/pages/css/img/Atual Clube - Marca_Laranja 1.svg';
import logo from '../auth/pages/css/img/logo.svg';
// Casco visual das páginas do associado (cp-*): importado, nunca recopiado — as
// duas telas públicas são a mesma identidade para quem recebe o link.
import '../checklist_publico/ChecklistPublicoPage.css';
import './PesquisaPublicaPage.css';

type Respostas = Record<string, string | string[]>;

/** Mensagem de erro da API, sem vazar detalhe técnico para o associado. */
const mensagemDoErro = (erro: any, padrao: string): string => {
  const doBackend = erro?.response?.data?.message;
  if (typeof doBackend === 'string' && doBackend.trim()) return doBackend;
  return padrao;
};

/**
 * Pesquisa de satisfação respondida pelo associado, sem login (`/pesquisa/:token`).
 *
 * Quem lê é o cliente, no celular: por isso hints inline em linguagem de cliente
 * final (`pp-hint`/`cp-hint`) em vez do drawer "Como Funciona" das telas internas
 * — mesma exceção deliberada da tela pública de checklist.
 */
const PesquisaPublicaPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<'checando' | 'valido' | 'invalido' | 'respondida'>('checando');
  const [erroDoLink, setErroDoLink] = useState('');
  const [phone, setPhone] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erroDaVerificacao, setErroDaVerificacao] = useState('');
  const [view, setView] = useState<PesquisaPublicaView | null>(null);
  const [telefoneVerificado, setTelefoneVerificado] = useState('');
  const [respostas, setRespostas] = useState<Respostas>({});
  const [enviando, setEnviando] = useState(false);
  const [erroDoEnvio, setErroDoEnvio] = useState('');
  const [concluida, setConcluida] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalido');
      setErroDoLink('Link de pesquisa inválido.');
      return;
    }

    getPesquisaPublicaInfo(token)
      .then((info) => setStatus(info.respondida ? 'respondida' : 'valido'))
      .catch((erro: any) => {
        setStatus('invalido');
        setErroDoLink(mensagemDoErro(erro, 'Link de pesquisa inválido ou expirado.'));
      });
  }, [token]);

  const handleVerificar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const digitos = phone.replace(/\D/g, '');
    if (digitos.length < 10) {
      setErroDaVerificacao('Informe o celular completo, com DDD.');
      return;
    }

    setVerificando(true);
    setErroDaVerificacao('');
    try {
      const dados = await verificarPesquisaPublica(token, digitos);
      setView(dados);
      setTelefoneVerificado(digitos);
    } catch (erro: any) {
      setErroDaVerificacao(mensagemDoErro(erro, 'Não foi possível verificar o número informado.'));
    } finally {
      setVerificando(false);
    }
  };

  const handleEnviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !view) return;

    const faltando = view.template.fields.find((campo) => {
      if (!campo.required) return false;
      const valor = respostas[campo.id];
      return valor === undefined || valor === '' || (Array.isArray(valor) && valor.length === 0);
    });
    if (faltando) {
      setErroDoEnvio(`Responda o campo "${faltando.label}" para enviar.`);
      return;
    }

    setEnviando(true);
    setErroDoEnvio('');
    try {
      // Campo sem valor não é enviado: string vazia em escala já derrubou o envio
      // inteiro uma vez (o `Number('')` que vale 0).
      const answers = view.template.fields
        .filter((campo) => {
          const valor = respostas[campo.id];
          return valor !== undefined && valor !== '' && !(Array.isArray(valor) && valor.length === 0);
        })
        .map((campo) => ({ fieldId: campo.id, value: respostas[campo.id] }));

      await responderPesquisaPublica(token, telefoneVerificado, answers);
      setConcluida(true);
    } catch (erro: any) {
      setErroDoEnvio(mensagemDoErro(erro, 'Não foi possível enviar suas respostas. Tente novamente.'));
    } finally {
      setEnviando(false);
    }
  };

  const mostrandoFormulario = Boolean(view) && !concluida;

  return (
    <div className="cp-page">
      <div className="logo-background">
        <img className="logo-background-atual" src={logoBackground} alt="logo de fundo" />
      </div>

      <div className="cp-container-wrapper">
        <img className="cp-logo" src={logo} alt="Logo Atual" />

        <div className={`cp-container ${!mostrandoFormulario ? 'cp-container--auth' : 'cp-container--full'}`}>
          <div className="cp-header">
            <h1 className="cp-title">Pesquisa de Satisfação</h1>

            {mostrandoFormulario && view && (
              <>
                <div className="cp-vehicle-card">
                  <div className="cp-vehicle-details">
                    <span className="cp-vehicle-label">Veículo</span>
                    <span className="cp-vehicle-name">{view.car.model || 'Veículo'}</span>
                  </div>
                  <div className="cp-plate-badge">
                    <div className="cp-plate-header">
                      <span>BRASIL</span>
                    </div>
                    <div className="cp-plate-number">{view.car.plate}</div>
                  </div>
                </div>

                <p className="cp-hint">
                  Esta pesquisa é só do seu atendimento. Evite encaminhar este link — quem abrir vai
                  precisar confirmar o mesmo número de celular cadastrado na oficina.
                </p>
              </>
            )}
          </div>

          {status === 'checando' && (
            <div className="cp-info cp-info--loading">
              <div className="cp-spinner"></div>
              <span>Validando link...</span>
            </div>
          )}

          {status === 'invalido' && <div className="cp-error-box">{erroDoLink}</div>}

          {status === 'respondida' && (
            <div className="pp-final">
              <h2 className="pp-final-titulo">Pesquisa já respondida</h2>
              <p className="pp-final-texto">
                As respostas deste atendimento já foram registradas. Obrigado pela participação!
              </p>
            </div>
          )}

          {status === 'valido' && !view && (
            <form className="cp-form-auth" onSubmit={handleVerificar}>
              <div className="cp-auth-div">
                <div className="cp-auth-title">
                  <h1 className="cp-h1-auth">Responder pesquisa</h1>
                  <p className="cp-p-auth">
                    Confirme o número do celular cadastrado na oficina para começar.
                  </p>
                  <p className="cp-hint">
                    Esta verificação garante que só quem passou pelo atendimento responda. Se o número
                    não for aceito, entre em contato com a oficina para atualizar o cadastro.
                  </p>
                </div>

                <div className="cp-auth-fields">
                  <p className="cp-field-label">Celular</p>

                  <div className="nice-form-group auth">
                    <input
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneBr(e.target.value))}
                      autoFocus
                      required
                      className="nice-input"
                    />
                  </div>
                </div>

                {erroDaVerificacao && <div className="cp-error-box">{erroDaVerificacao}</div>}

                <div className="cp-button-div auth">
                  <button type="submit" disabled={verificando} className="cp-btn-primary">
                    {verificando ? 'Verificando...' : 'Começar'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {concluida && (
            <div className="pp-final">
              <h2 className="pp-final-titulo">Respostas enviadas</h2>
              <p className="pp-final-texto">
                Obrigado por avaliar o atendimento. Sua opinião ajuda a oficina a melhorar.
              </p>
            </div>
          )}

          {mostrandoFormulario && view && (
            <form className="pp-formulario" onSubmit={handleEnviar}>
              {view.template.description && <p className="pp-descricao">{view.template.description}</p>}

              <p className="cp-hint">
                Suas respostas vão direto para a equipe da oficina. Você pode responder uma única vez,
                e só os campos marcados como obrigatórios precisam ser preenchidos.
              </p>

              {view.template.fields.map((campo) => (
                <CampoDaPesquisa
                  key={campo.id}
                  campo={campo}
                  valor={respostas[campo.id]}
                  onChange={(valor) => setRespostas((atual) => ({ ...atual, [campo.id]: valor }))}
                />
              ))}

              {erroDoEnvio && <div className="cp-error-box">{erroDoEnvio}</div>}

              <div className="cp-button-div">
                <button type="submit" disabled={enviando} className="cp-btn-primary">
                  {enviando ? 'Enviando...' : 'Enviar respostas'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PesquisaPublicaPage;
